import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import useItemDialogFocus from "../useItemDialogFocus";
import "./ItemClassificationPicker.css";
import "./CategoryPicker.css";

export const categoryBranch = (category) => [
  category?.subfamily?.family?.name,
  category?.subfamily?.name,
].filter(Boolean).join(" / ");

const normalizeSearch = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();

export default function CategoryPicker({ categories, value, onChange, onClose }) {
  const { themeId, languageId } = usePmsTheme();
  const t = useCatalogTaxonomyText();
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const dialogRef = useItemDialogFocus(true, false, onClose);
  useEffect(() => { searchRef.current?.focus({ preventScroll: true }); }, []);
  const terms = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  const options = categories.filter((category) => {
    const text = normalizeSearch([category.name, category.code, categoryBranch(category)].join(" "));
    return terms.every((term) => text.includes(term));
  }).sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""), languageId || undefined, { sensitivity: "base", numeric: true }));

  return createPortal(
    <div className="pilcore-purchasing-ui icp-overlay category-picker" data-theme={themeId}
      onClick={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) onClose(); }}>
      <section className="icp-dialog" ref={dialogRef} role="dialog" aria-modal="true"
        aria-label={t("Select a category")} tabIndex={-1}>
        <header>
          <h3>{t("Select a category")}</h3>
          <button type="button" className="icp-button" onClick={onClose}>{t("Close")}</button>
        </header>
        <div className="cp-search">
          <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)}
            aria-label={t("Search by name, code, family or subfamily...")}
            placeholder={t("Search by name, code, family or subfamily...")} />
        </div>
        <div className="icp-content">
          <div className="icp-cards">
            {options.map((category) => (
              <button type="button" className="icp-card" key={category.id}
                aria-pressed={String(value) === String(category.id)}
                onClick={() => { onChange(String(category.id)); onClose(); }}>
                <strong>{category.name}</strong>
                {category.code && <small>{t("Code")}: {category.code}</small>}
                {categoryBranch(category) && <small>{categoryBranch(category)}</small>}
              </button>
            ))}
          </div>
          {!options.length && <p role="status">{t("No categories match the current search.")}</p>}
        </div>
      </section>
    </div>,
    document.body
  );
}
